import React, { useState, useEffect, useRef } from 'react';
import {
  ChevronDown,
  CheckCircle,
  AlertCircle,
  FileDown,
  Loader2,
  Paperclip,
  X,
} from 'lucide-react';
import { invoiceAPI, establishmentAPI } from '../api/apiService';
import { useAuth, useActiveEstId } from './AuthContext';
import DateInput from './DateInput';
import './UpdateEmployee.css';

const GenerateInvoice = () => {
  const { user } = useAuth();
  const lockedEstId = useActiveEstId();

  // State management
  const [establishments, setEstablishments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedInvoice, setGeneratedInvoice] = useState(null);
  const [error, setError] = useState('');
  const [pdfLoading, setPdfLoading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const successSectionRef = useRef(null);

  // Form state
  const [formData, setFormData] = useState({
    establishment_id: lockedEstId || '',
    invoice_date: new Date().toISOString().split('T')[0],
    billing_period_start: '',
    billing_period_end: '',
    days_in_month: 30,
    due_date: '',
    work_order_no: '',
    // Client/Service Receiver
    client_name: '',
    client_address: '',
    client_city: '',
    client_state: '',
    client_pincode: '',
    client_gstin: '',
    client_contact: '',
    client_email: '',
    place_of_supply: '',
    // Service Details
    sac_code: '998511',
    service_description: 'SECURITY SERVICES',
    // Charges & Tax
    service_charge_rate: 10,
    discount_rate: 0,
    discount_amount: 0,
    is_igst: false,
    cgst_rate: 9,
    sgst_rate: 9,
    igst_rate: 18,
    // Payment Details
    bank_name: '',
    bank_account_number: '',
    bank_ifsc: '',
    bank_branch: '',
    payment_terms: 'INVOICE DUES TO BE CLEARED IN 15 WORKING DAYS. DELAYED PAYMENTS WILL ATTRACT A PENAL INTEREST OF 18% PER ANNUM.',
    // Additional
    notes: '',
    terms_conditions: 'As per Government Notification Section 194C TDS is applicable 1.5% on Billing Amount @ 2% in case of Partnership Firm.',
    tds_applicable: true,
    tds_rate: 1.5,
    reverse_charge: false,
  });

  // Selected establishment details
  const [selectedEstablishment, setSelectedEstablishment] = useState(null);

  // Month options
  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ];

  const currentYear = new Date().getFullYear();
  const years = [currentYear, currentYear - 1, currentYear - 2];

  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(currentYear);

  // Fetch establishments on mount
  useEffect(() => {
    const fetchEstablishments = async () => {
      try {
        setLoading(true);
        const response = await establishmentAPI.getAll();
        const estList = response.establishments || [];
        setEstablishments(estList);

        // For ClientAdmin, auto-populate using their locked est
        if (lockedEstId) {
          const est = estList.find(e => String(e.id) === lockedEstId) || estList[0];
          if (est) {
            setSelectedEstablishment(est);
            setFormData((prev) => ({
              ...prev,
              establishment_id: String(est.id),
              client_name: est?.name || '',
              client_address: est?.address || '',
              client_city: est?.city || '',
              client_state: est?.state || '',
              client_pincode: est?.zip_code || '',
              client_gstin: est?.gstin || '',
              client_contact: est?.main_telephone || est?.employer_telephone || '',
              client_email: est?.main_email || '',
              place_of_supply: est?.state || '',
              bank_name: est?.bank_name || '',
              bank_account_number: est?.bank_account_number || '',
              bank_ifsc: est?.bank_ifsc || '',
              bank_branch: est?.bank_branch || '',
            }));
          }
        }
      } catch (error) {
        console.error('Error fetching organizations:', error);
        setError('Error loading organizations: ' + error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchEstablishments();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync when MultiClientAdmin switches org
  useEffect(() => {
    if (!lockedEstId || establishments.length === 0) return;
    const est = establishments.find(e => String(e.id) === lockedEstId);
    if (est) {
      setSelectedEstablishment(est);
      setFormData(prev => ({
        ...prev,
        establishment_id: String(est.id),
        client_name: est.name || '',
        client_address: est.address || '',
        client_city: est.city || '',
        client_state: est.state || '',
        client_pincode: est.zip_code || '',
        client_gstin: est.gstin || '',
        client_contact: est.main_telephone || est.employer_telephone || '',
        client_email: est.main_email || '',
        place_of_supply: est.state || '',
        bank_name: est.bank_name || '',
        bank_account_number: est.bank_account_number || '',
        bank_ifsc: est.bank_ifsc || '',
        bank_branch: est.bank_branch || '',
      }));
    }
  }, [lockedEstId, establishments]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-select first establishment when establishments are loaded (SuperAdmin only)
  useEffect(() => {
    if (lockedEstId) return;
    if (establishments.length > 0 && !formData.establishment_id) {
      const firstEstablishment = establishments[0];

      setSelectedEstablishment(firstEstablishment);

      setFormData((prev) => ({
        ...prev,
        establishment_id: firstEstablishment.id.toString(),
        // Client details from establishment (can be edited)
        client_name: firstEstablishment?.name || '',
        client_address: firstEstablishment?.address || '',
        client_city: firstEstablishment?.city || '',
        client_state: firstEstablishment?.state || '',
        client_pincode: firstEstablishment?.zip_code || '',
        client_gstin: firstEstablishment?.gstin || '',
        client_contact: firstEstablishment?.main_telephone || firstEstablishment?.employer_telephone || '',
        client_email: firstEstablishment?.main_email || '',
        place_of_supply: firstEstablishment?.state || '',
        // Bank details from establishment
        bank_name: firstEstablishment?.bank_name || '',
        bank_account_number: firstEstablishment?.bank_account_number || '',
        bank_ifsc: firstEstablishment?.bank_ifsc || '',
        bank_branch: firstEstablishment?.bank_branch || '',
      }));
    }
  }, [establishments]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update billing period when month/year changes
  useEffect(() => {
    const startDate = new Date(selectedYear, selectedMonth - 1, 1);
    const endDate = new Date(selectedYear, selectedMonth, 0);
    const dueDate = new Date(selectedYear, selectedMonth, 15);
    const daysInMonth = endDate.getDate();

    setFormData((prev) => ({
      ...prev,
      billing_period_start: startDate.toISOString().split('T')[0],
      billing_period_end: endDate.toISOString().split('T')[0],
      due_date: dueDate.toISOString().split('T')[0],
      days_in_month: daysInMonth,
    }));
  }, [selectedMonth, selectedYear]);

  // Handle establishment change - auto-fill details
  const handleEstablishmentChange = (e) => {
    const value = e.target.value;
    const establishment = establishments.find((est) => est.id === parseInt(value));

    setSelectedEstablishment(establishment);

    setFormData((prev) => ({
      ...prev,
      establishment_id: value,
      // Client details from establishment (can be edited)
      client_name: establishment?.name || '',
      client_address: establishment?.address || '',
      client_city: establishment?.city || '',
      client_state: establishment?.state || '',
      client_pincode: establishment?.zip_code || '',
      client_gstin: establishment?.gstin || '',
      client_contact: establishment?.main_telephone || establishment?.employer_telephone || '',
      client_email: establishment?.main_email || '',
      place_of_supply: establishment?.state || '',
      // Bank details from establishment
      bank_name: establishment?.bank_name || '',
      bank_account_number: establishment?.bank_account_number || '',
      bank_ifsc: establishment?.bank_ifsc || '',
      bank_branch: establishment?.bank_branch || '',
    }));
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleGenerateInvoice = async () => {
    if (!formData.establishment_id) {
      setError('Please select an organization');
      return;
    }

    if (!formData.client_name) {
      setError('Please enter client name');
      return;
    }

    setIsGenerating(true);
    setError('');

    try {
      // Prepare attachment keys from uploaded files
      const attachmentKeys = uploadedFiles.map(file => file.s3_key);

      const response = await invoiceAPI.generate({
        ...formData,
        establishment_id: parseInt(formData.establishment_id),
        service_charge_rate: parseFloat(formData.service_charge_rate) || 0,
        attachment_keys: attachmentKeys,
      });

      setGeneratedInvoice(response);

      // Show success popup
      setSuccessMessage('Invoice successfully generated!');
      setShowSuccessPopup(true);
      setTimeout(() => setShowSuccessPopup(false), 3000);

      // Scroll to success section after it renders
      setTimeout(() => {
        if (successSectionRef.current) {
          successSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 200);
    } catch (error) {
      console.error('Error generating invoice:', error);
      setError('Error generating invoice: ' + error.message);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownloadAllFiles = async () => {
    if (!generatedInvoice?.invoice_id) return;

    try {
      setPdfLoading(true);

      // Get S3 keys from uploaded files
      const attachmentKeys = uploadedFiles.map(file => file.s3_key);

      // Download all files as ZIP
      const blob = await invoiceAPI.downloadAllFiles(generatedInvoice.invoice_id, attachmentKeys);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Invoice_${generatedInvoice.invoice_number.replace(/\//g, '_')}_All_Files.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) {
      console.error('Error downloading files:', error);
      setError('Error downloading files: ' + error.message);
    } finally {
      setPdfLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const getMonthLabel = () => {
    const month = months.find((m) => m.value === parseInt(selectedMonth));
    return month ? `${month.label} ${selectedYear}` : `${selectedMonth}/${selectedYear}`;
  };

  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    if (!formData.establishment_id) {
      setError('Please select an organization first');
      return;
    }

    setIsUploading(true);
    setError('');

    try {
      const uploadedFilesList = [];

      for (const file of files) {
        const result = await invoiceAPI.uploadAttachment(file, formData.establishment_id);
        uploadedFilesList.push({
          name: file.name,
          s3_url: result.s3_url,
          s3_key: result.s3_key,
          file_size: result.file_size
        });
      }

      setUploadedFiles(prev => [...prev, ...uploadedFilesList]);

      // Show success popup
      setSuccessMessage(`Successfully uploaded ${uploadedFilesList.length} file(s) to S3!`);
      setShowSuccessPopup(true);
      setTimeout(() => setShowSuccessPopup(false), 3000);
    } catch (err) {
      console.error('File upload error:', err);
      setError('Failed to upload file: ' + err.message);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const removeFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="new-employee-container">
      <div className="new-employee-header-section" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h2 className="new-employee-header">
            Generate Invoice
          </h2>
          <p style={{ color: '#6b7280', fontSize: '0.95rem', marginTop: '8px' }}>
            Create professional invoices for organization services
          </p>
        </div>

        {/* File Upload Button */}
        <div>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            multiple
            accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading || !formData.establishment_id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.625rem 1rem',
              background: formData.establishment_id ? '#2563eb' : '#d1d5db',
              color: '#fff',
              border: 'none',
              borderRadius: '0.375rem',
              fontSize: '0.875rem',
              fontWeight: '500',
              cursor: formData.establishment_id ? 'pointer' : 'not-allowed',
              transition: 'all 0.2s',
            }}
            title={!formData.establishment_id ? 'Select an organization first' : 'Attach files'}
          >
            <Paperclip size={16} />
            {isUploading ? 'Uploading...' : 'Attach Files'}
          </button>
        </div>
      </div>

      {/* Display uploaded files */}
      {uploadedFiles.length > 0 && (
        <div style={{
          marginBottom: '1.5rem',
          padding: '1rem',
          background: '#f0f9ff',
          border: '1px solid #bae6fd',
          borderRadius: '0.5rem',
        }}>
          <div style={{ fontWeight: '600', marginBottom: '0.5rem', fontSize: '0.875rem', color: '#0369a1' }}>
            Attached Files ({uploadedFiles.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {uploadedFiles.map((file, index) => (
              <div key={index} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '0.5rem',
                background: 'var(--theme-surface, #ffffff)',
                borderRadius: '0.25rem',
                fontSize: '0.875rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Paperclip size={14} />
                  <a href={file.s3_url} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', textDecoration: 'none' }}>
                    {file.name}
                  </a>
                  <span style={{ color: '#6b7280', fontSize: '0.75rem' }}>
                    ({(file.file_size / 1024).toFixed(2)} KB)
                  </span>
                </div>
                <button
                  onClick={() => removeFile(index)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#dc2626',
                    padding: '0.25rem',
                  }}
                  title="Remove file"
                >
                  <X size={16} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="new-employee-form">
        {error && (
          <div
            style={{
              padding: '1rem',
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '0.5rem',
              color: '#dc2626',
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            <AlertCircle size={20} />
            {error}
          </div>
        )}

        {/* Establishment & Period Selection */}
        <div className="new-employee-content">
          <h3 className="new-employee-section-header">Invoice Parameters</h3>
          <p className="new-employee-section-description">
            Select organization and billing period
          </p>

          <div className="new-employee-field-group" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.5rem' }}>
            {/* Establishment */}
            {!lockedEstId && (
            <div>
              <label className="new-employee-field-label">
                Organization <span className="new-employee-required">*</span>
              </label>
              <div style={{ position: 'relative' }}>
                <select
                  value={formData.establishment_id}
                  onChange={handleEstablishmentChange}
                  className="new-employee-input"
                  disabled={loading}
                  style={{ appearance: 'none', paddingRight: '2.5rem' }}
                >
                  {establishments.map((est) => (
                    <option key={est.id} value={est.id}>
                      {est.name}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  size={20}
                  style={{
                    position: 'absolute',
                    right: '0.75rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    pointerEvents: 'none',
                    color: '#6b7280',
                  }}
                />
              </div>
            </div>
            )}

            {/* Invoice Date */}
            <div>
              <label className="new-employee-field-label">
                Date of Invoice <span className="new-employee-required">*</span>
              </label>
              <DateInput
                name="invoice_date"
                value={formData.invoice_date}
                onChange={handleInputChange}
                className="new-employee-input"
              />
            </div>
          </div>

          <div className="new-employee-field-group" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.5rem' }}>
            {/* Billing Period - Month */}
            <div>
              <label className="new-employee-field-label">
                Billing Month <span className="new-employee-required">*</span>
              </label>
              <div style={{ position: 'relative' }}>
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                  className="new-employee-input"
                  style={{ appearance: 'none', paddingRight: '2.5rem' }}
                >
                  {months.map((month) => (
                    <option key={month.value} value={month.value}>
                      {month.label}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  size={20}
                  style={{
                    position: 'absolute',
                    right: '0.75rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    pointerEvents: 'none',
                    color: '#6b7280',
                  }}
                />
              </div>
            </div>

            {/* Year */}
            <div>
              <label className="new-employee-field-label">
                Year <span className="new-employee-required">*</span>
              </label>
              <div style={{ position: 'relative' }}>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  className="new-employee-input"
                  style={{ appearance: 'none', paddingRight: '2.5rem' }}
                >
                  {years.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  size={20}
                  style={{
                    position: 'absolute',
                    right: '0.75rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    pointerEvents: 'none',
                    color: '#6b7280',
                  }}
                />
              </div>
            </div>
          </div>

          <div className="new-employee-field-group" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.5rem' }}>
            {/* Days in Month */}
            <div>
              <label className="new-employee-field-label">Days In Month</label>
              <input
                type="number"
                name="days_in_month"
                value={formData.days_in_month}
                onChange={handleInputChange}
                className="new-employee-input"
                readOnly
                style={{ background: 'var(--theme-surfaceHover)' }}
              />
            </div>

            {/* Due Date */}
            <div>
              <label className="new-employee-field-label">Due Date</label>
              <DateInput
                name="due_date"
                value={formData.due_date}
                onChange={handleInputChange}
                className="new-employee-input"
              />
            </div>
          </div>

          <div className="new-employee-field-group" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.5rem' }}>
            {/* Work Order No */}
            <div>
              <label className="new-employee-field-label">Work Order No</label>
              <input
                type="text"
                name="work_order_no"
                value={formData.work_order_no}
                onChange={handleInputChange}
                className="new-employee-input"
                placeholder="e.g., WO/2024/001"
              />
            </div>

            {/* Place of Supply */}
            <div>
              <label className="new-employee-field-label">Place of Supply</label>
              <input
                type="text"
                name="place_of_supply"
                value={formData.place_of_supply}
                onChange={handleInputChange}
                className="new-employee-input"
                placeholder="State for GST"
              />
            </div>
          </div>
        </div>

        {/* Service Provider Info - Display Only */}
        {selectedEstablishment && (
          <div className="new-employee-content">
            <h3 className="new-employee-section-header">Service Provider Details</h3>
            <p className="new-employee-section-description">
              Your company details (from organization)
            </p>
            <div style={{
              background: 'var(--theme-surface)',
              padding: '1rem',
              borderRadius: '0.5rem',
              border: '1px solid #e2e8f0'
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.75rem', fontSize: '0.875rem' }}>
                <div><strong>GSTIN:</strong> {selectedEstablishment.gstin || 'N/A'}</div>
                <div><strong>PAN:</strong> {selectedEstablishment.pan || 'N/A'}</div>
                <div><strong>ESIC Code:</strong> {selectedEstablishment.esic_code || 'N/A'}</div>
                <div><strong>EPF Code:</strong> {selectedEstablishment.epf_code || 'N/A'}</div>
              </div>
            </div>
          </div>
        )}

        {/* Client Details */}
        <div className="new-employee-content">
          <h3 className="new-employee-section-header">Client / Bill To Details</h3>
          <p className="new-employee-section-description">
            Client information for the invoice
          </p>

          <div className="new-employee-field-group" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.5rem' }}>
            <div>
              <label className="new-employee-field-label">
                Client Name <span className="new-employee-required">*</span>
              </label>
              <input
                type="text"
                name="client_name"
                value={formData.client_name}
                onChange={handleInputChange}
                className="new-employee-input"
                placeholder="Enter client name"
              />
            </div>

            <div>
              <label className="new-employee-field-label">
                GSTIN
              </label>
              <input
                type="text"
                name="client_gstin"
                value={formData.client_gstin}
                onChange={handleInputChange}
                className="new-employee-input"
                placeholder="e.g., 29XXXXXXXXXXXXXXX"
              />
            </div>
          </div>

          <div className="new-employee-field-group" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.5rem' }}>
            <div style={{ gridColumn: '1 / -1' }}>
              <label className="new-employee-field-label">
                Address
              </label>
              <textarea
                name="client_address"
                value={formData.client_address}
                onChange={handleInputChange}
                className="new-employee-input"
                rows="2"
                placeholder="Enter client address"
                style={{ resize: 'vertical' }}
              />
            </div>
          </div>

          <div className="new-employee-field-group" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.5rem' }}>
            <div>
              <label className="new-employee-field-label">City</label>
              <input
                type="text"
                name="client_city"
                value={formData.client_city}
                onChange={handleInputChange}
                className="new-employee-input"
                placeholder="City"
              />
            </div>

            <div>
              <label className="new-employee-field-label">State</label>
              <input
                type="text"
                name="client_state"
                value={formData.client_state}
                onChange={handleInputChange}
                className="new-employee-input"
                placeholder="State"
              />
            </div>
          </div>

          <div className="new-employee-field-group" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.5rem' }}>
            <div>
              <label className="new-employee-field-label">Pincode</label>
              <input
                type="text"
                name="client_pincode"
                value={formData.client_pincode}
                onChange={handleInputChange}
                className="new-employee-input"
                placeholder="Pincode"
              />
            </div>

            <div>
              <label className="new-employee-field-label">
                Contact Number
              </label>
              <input
                type="text"
                name="client_contact"
                value={formData.client_contact}
                onChange={handleInputChange}
                className="new-employee-input"
                placeholder="Phone number"
              />
            </div>
          </div>

          <div className="new-employee-field-group" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.5rem' }}>
            <div>
              <label className="new-employee-field-label">
                Email
              </label>
              <input
                type="email"
                name="client_email"
                value={formData.client_email}
                onChange={handleInputChange}
                className="new-employee-input"
                placeholder="email@example.com"
              />
            </div>
          </div>
        </div>

        {/* Service Details */}
        <div className="new-employee-content">
          <h3 className="new-employee-section-header">Service Details</h3>
          <p className="new-employee-section-description">
            Service description and SAC code
          </p>

          <div className="new-employee-field-group" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.5rem' }}>
            <div>
              <label className="new-employee-field-label">
                SAC Code <span className="new-employee-required">*</span>
              </label>
              <input
                type="text"
                name="sac_code"
                value={formData.sac_code}
                onChange={handleInputChange}
                className="new-employee-input"
                placeholder="e.g., 998511"
              />
              <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                998511 - Security Services, 998512 - Investigation Services
              </p>
            </div>

            <div>
              <label className="new-employee-field-label">
                Service Description <span className="new-employee-required">*</span>
              </label>
              <input
                type="text"
                name="service_description"
                value={formData.service_description}
                onChange={handleInputChange}
                className="new-employee-input"
                placeholder="e.g., SECURITY SERVICES"
              />
            </div>
          </div>
        </div>

        {/* Service Charges & Tax Details */}
        <div className="new-employee-content">
          <h3 className="new-employee-section-header">Charges & Tax</h3>
          <p className="new-employee-section-description">
            Configure service charges, discount and GST settings
          </p>

          <div className="new-employee-field-group" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.5rem' }}>
            <div>
              <label className="new-employee-field-label">
                Service Charge Rate (%)
              </label>
              <input
                type="number"
                name="service_charge_rate"
                value={formData.service_charge_rate}
                onChange={handleInputChange}
                className="new-employee-input"
                min="0"
                max="100"
                step="0.5"
              />
            </div>

            <div>
              <label className="new-employee-field-label">
                Discount Rate (%)
              </label>
              <input
                type="number"
                name="discount_rate"
                value={formData.discount_rate}
                onChange={handleInputChange}
                className="new-employee-input"
                min="0"
                max="100"
                step="0.5"
              />
            </div>
          </div>

          <div className="new-employee-field-group" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.5rem' }}>
            <div>
              <label className="new-employee-field-label">
                GST Type
              </label>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1.5rem',
                  marginTop: '0.5rem',
                }}
              >
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="radio"
                    name="is_igst"
                    checked={!formData.is_igst}
                    onChange={() => setFormData((prev) => ({ ...prev, is_igst: false }))}
                  />
                  <span>CGST + SGST (Intra-State)</span>
                </label>
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="radio"
                    name="is_igst"
                    checked={formData.is_igst}
                    onChange={() => setFormData((prev) => ({ ...prev, is_igst: true }))}
                  />
                  <span>IGST (Inter-State)</span>
                </label>
              </div>
            </div>
          </div>

          <div className="new-employee-field-group" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.5rem' }}>
            {!formData.is_igst ? (
              <>
                <div>
                  <label className="new-employee-field-label">CGST Rate (%)</label>
                  <input
                    type="number"
                    name="cgst_rate"
                    value={formData.cgst_rate}
                    onChange={handleInputChange}
                    className="new-employee-input"
                    min="0"
                    max="50"
                    step="0.5"
                  />
                </div>
                <div>
                  <label className="new-employee-field-label">SGST Rate (%)</label>
                  <input
                    type="number"
                    name="sgst_rate"
                    value={formData.sgst_rate}
                    onChange={handleInputChange}
                    className="new-employee-input"
                    min="0"
                    max="50"
                    step="0.5"
                  />
                </div>
              </>
            ) : (
              <div>
                <label className="new-employee-field-label">IGST Rate (%)</label>
                <input
                  type="number"
                  name="igst_rate"
                  value={formData.igst_rate}
                  onChange={handleInputChange}
                  className="new-employee-input"
                  min="0"
                  max="50"
                  step="0.5"
                />
              </div>
            )}
          </div>

          <div className="new-employee-field-group" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.5rem' }}>
            <div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  name="reverse_charge"
                  checked={formData.reverse_charge}
                  onChange={handleInputChange}
                />
                <span className="new-employee-field-label" style={{ marginBottom: 0 }}>
                  Tax Subject to Reverse Charge
                </span>
              </label>
            </div>
          </div>
        </div>

        {/* Payment Details */}
        <div className="new-employee-content">
          <h3 className="new-employee-section-header">Payment Details</h3>
          <p className="new-employee-section-description">
            Bank details for payment
          </p>

          <div className="new-employee-field-group" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.5rem' }}>
            <div>
              <label className="new-employee-field-label">Bank Name</label>
              <input
                type="text"
                name="bank_name"
                value={formData.bank_name}
                onChange={handleInputChange}
                className="new-employee-input"
                placeholder="e.g., PUNJAB NATIONAL BANK"
              />
            </div>

            <div>
              <label className="new-employee-field-label">Branch</label>
              <input
                type="text"
                name="bank_branch"
                value={formData.bank_branch}
                onChange={handleInputChange}
                className="new-employee-input"
                placeholder="Branch name"
              />
            </div>
          </div>

          <div className="new-employee-field-group" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.5rem' }}>
            <div>
              <label className="new-employee-field-label">Account Number</label>
              <input
                type="text"
                name="bank_account_number"
                value={formData.bank_account_number}
                onChange={handleInputChange}
                className="new-employee-input"
                placeholder="Account number"
              />
            </div>

            <div>
              <label className="new-employee-field-label">IFSC Code</label>
              <input
                type="text"
                name="bank_ifsc"
                value={formData.bank_ifsc}
                onChange={handleInputChange}
                className="new-employee-input"
                placeholder="e.g., PUNB0123456"
              />
            </div>
          </div>

          <div className="new-employee-field-group" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.5rem' }}>
            <div style={{ gridColumn: '1 / -1' }}>
              <label className="new-employee-field-label">Payment Terms</label>
              <textarea
                name="payment_terms"
                value={formData.payment_terms}
                onChange={handleInputChange}
                className="new-employee-input"
                rows="2"
                style={{ resize: 'vertical' }}
              />
            </div>
          </div>
        </div>

        {/* TDS Details */}
        <div className="new-employee-content">
          <h3 className="new-employee-section-header">TDS Information</h3>

          <div className="new-employee-field-group" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.5rem' }}>
            <div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  name="tds_applicable"
                  checked={formData.tds_applicable}
                  onChange={handleInputChange}
                />
                <span className="new-employee-field-label" style={{ marginBottom: 0 }}>
                  TDS Applicable (Section 194C)
                </span>
              </label>
            </div>

            {formData.tds_applicable && (
              <div>
                <label className="new-employee-field-label">TDS Rate (%)</label>
                <input
                  type="number"
                  name="tds_rate"
                  value={formData.tds_rate}
                  onChange={handleInputChange}
                  className="new-employee-input"
                  min="0"
                  max="30"
                  step="0.5"
                />
                <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                  1.5% for Companies, 2% for Partnership Firms
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Additional Notes */}
        <div className="new-employee-content">
          <h3 className="new-employee-section-header">Additional Information</h3>

          <div className="new-employee-field-group" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.5rem' }}>
            <div style={{ gridColumn: '1 / -1' }}>
              <label className="new-employee-field-label">Notes</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                className="new-employee-input"
                rows="2"
                placeholder="Additional notes for the invoice"
                style={{ resize: 'vertical' }}
              />
            </div>
          </div>

          <div className="new-employee-field-group" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.5rem' }}>
            <div style={{ gridColumn: '1 / -1' }}>
              <label className="new-employee-field-label">Terms & Conditions</label>
              <textarea
                name="terms_conditions"
                value={formData.terms_conditions}
                onChange={handleInputChange}
                className="new-employee-input"
                rows="2"
                style={{ resize: 'vertical' }}
              />
            </div>
          </div>
        </div>

        {/* Generate Button */}
        <div style={{ marginTop: '2rem', padding: '0 1.5rem' }}>
          <button
            onClick={handleGenerateInvoice}
            disabled={isGenerating || !formData.establishment_id}
            className="new-employee-btn-create"
            style={{ width: '100%' }}
          >
            {isGenerating ? (
              <>
                <div className="new-employee-spinner"></div>
                Generating Invoice...
              </>
            ) : (
              <>
                Generate Invoice - {getMonthLabel()}
              </>
            )}
          </button>
        </div>

        {/* Generated Invoice Success */}
        {generatedInvoice && (
          <div ref={successSectionRef} style={{ marginTop: '2rem', padding: '0 1.5rem' }}>
            <div
              style={{
                padding: '1.5rem',
                background: '#f0fdf4',
                border: '1.5px solid #10b981',
                borderRadius: '0.5rem',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  marginBottom: '1rem',
                }}
              >
                <CheckCircle size={24} style={{ color: '#10b981' }} />
                <h4 style={{ color: '#059669', fontWeight: '600', margin: 0 }}>
                  Invoice Generated Successfully
                </h4>
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: '1rem',
                }}
              >
                <div>
                  <p style={{ color: '#065f46', fontSize: '0.875rem', margin: '0.25rem 0' }}>
                    <strong>Invoice Number:</strong> {generatedInvoice.invoice_number}
                  </p>
                  <p style={{ color: '#065f46', fontSize: '0.875rem', margin: '0.25rem 0' }}>
                    <strong>Client:</strong> {generatedInvoice.client_name}
                  </p>
                  <p style={{ color: '#065f46', fontSize: '0.875rem', margin: '0.25rem 0' }}>
                    <strong>Total Employees:</strong> {generatedInvoice.total_employees}
                  </p>
                  <p style={{ color: '#065f46', fontSize: '0.875rem', margin: '0.25rem 0' }}>
                    <strong>Taxable Value:</strong> {formatCurrency(generatedInvoice.gross_amount)}
                  </p>
                </div>
                <div>
                  <p style={{ color: '#065f46', fontSize: '0.875rem', margin: '0.25rem 0' }}>
                    <strong>Service Charges:</strong> {formatCurrency(generatedInvoice.service_charge_amount)}
                  </p>
                  {generatedInvoice.discount_amount > 0 && (
                    <p style={{ color: '#065f46', fontSize: '0.875rem', margin: '0.25rem 0' }}>
                      <strong>Discount:</strong> {formatCurrency(generatedInvoice.discount_amount)}
                    </p>
                  )}
                  <p style={{ color: '#065f46', fontSize: '0.875rem', margin: '0.25rem 0' }}>
                    <strong>Total Taxable:</strong> {formatCurrency(generatedInvoice.subtotal)}
                  </p>
                  {generatedInvoice.cgst_amount > 0 && (
                    <p style={{ color: '#065f46', fontSize: '0.875rem', margin: '0.25rem 0' }}>
                      <strong>CGST:</strong> {formatCurrency(generatedInvoice.cgst_amount)} |{' '}
                      <strong>SGST:</strong> {formatCurrency(generatedInvoice.sgst_amount)}
                    </p>
                  )}
                  {generatedInvoice.igst_amount > 0 && (
                    <p style={{ color: '#065f46', fontSize: '0.875rem', margin: '0.25rem 0' }}>
                      <strong>IGST:</strong> {formatCurrency(generatedInvoice.igst_amount)}
                    </p>
                  )}
                  <p
                    style={{
                      color: '#059669',
                      fontSize: '1.125rem',
                      margin: '0.5rem 0',
                      fontWeight: '700',
                      borderTop: '1px solid #10b981',
                      paddingTop: '0.5rem',
                    }}
                  >
                    <strong>Grand Total:</strong> {formatCurrency(generatedInvoice.grand_total)}
                  </p>
                </div>
              </div>

              {generatedInvoice.amount_in_words && (
                <p
                  style={{
                    color: '#065f46',
                    fontSize: '0.875rem',
                    marginTop: '1rem',
                    fontStyle: 'italic',
                  }}
                >
                  {generatedInvoice.amount_in_words}
                </p>
              )}

              {/* PDF Download Buttons */}
              <div
                style={{
                  display: 'flex',
                  gap: '1rem',
                  marginTop: '1.5rem',
                  paddingTop: '1rem',
                  borderTop: '1px solid #bbf7d0',
                }}
              >
                <button
                  onClick={handleDownloadAllFiles}
                  disabled={pdfLoading}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.75rem 1.5rem',
                    background: '#059669',
                    border: 'none',
                    borderRadius: '0.5rem',
                    color: '#fff',
                    fontWeight: '600',
                    cursor: pdfLoading ? 'wait' : 'pointer',
                    opacity: pdfLoading ? 0.7 : 1,
                  }}
                >
                  {pdfLoading ? (
                    <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
                  ) : (
                    <FileDown size={18} />
                  )}
                  Download All Files (ZIP)
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Success Popup */}
      {showSuccessPopup && (
        <div
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            background: '#10b981',
            color: '#fff',
            padding: '1rem 1.5rem',
            borderRadius: '0.5rem',
            boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            animation: 'slideInRight 0.3s ease-out',
            minWidth: '300px',
          }}
        >
          <CheckCircle size={24} />
          <div>
            <div style={{ fontWeight: '600', fontSize: '1rem' }}>Success!</div>
            <div style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>{successMessage}</div>
          </div>
        </div>
      )}

      <style>
        {`
          @keyframes slideInRight {
            from {
              transform: translateX(100%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
        `}
      </style>
    </div>
  );
};

export default GenerateInvoice;
